import type { CodeSuggestion } from '@wms/domain';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { api } from '../api';
import { useOptionalSession } from '../session';
import { moveHighlight, SUGGEST_IDLE_MS, SUGGEST_MIN_CHARS, SUGGESTION_KIND_LABELS, suggestionText, suggestPath, type SuggestSource } from '../suggest';

export interface CodeSuggestions {
  readonly items: readonly CodeSuggestion[];
  /** the highlighted row; -1: none (Enter sends the typed text) */
  readonly active: number;
  readonly open: boolean;
  /** the field's keys while the list is open: the arrows move, Enter picks the highlighted row, Escape closes. True when the key was the list's. */
  onKey(event: KeyboardEvent<HTMLInputElement>, pick: (s: CodeSuggestion) => void): boolean;
  /** the list closes and stays closed for this text (default: the field's text now; a pick passes the text it puts in the field) */
  close(value?: string): void;
  /** for the field: what screen readers need to know of the list */
  readonly inputProps: Record<string, string | boolean | undefined>;
  readonly listId: string;
  readonly setActive: (index: number) => void;
}

/**
 * Asks /api/wms/suggest what a code a manager is typing may mean, once the field stands still
 * (SUGGEST_IDLE_MS) and only when the text came at a person's pace (`byPerson`): a reader's burst
 * ends with Enter long before, a Bluetooth reader's pause still looks like a reader. Nothing for the
 * warehouse staff (the API refuses them too), nothing without a `source`. The answer to an older
 * text is dropped; a failed question shows nothing (the field works as before).
 */
export function useCodeSuggestions(opts: { readonly source: SuggestSource | undefined; readonly enabled: boolean; readonly value: string; readonly byPerson: () => boolean }): CodeSuggestions {
  const session = useOptionalSession();
  const warehouseId = session?.warehouseId ?? null;
  const allowed = Boolean(opts.source && opts.enabled && session?.isSupervisor && warehouseId);
  const [items, setItems] = useState<readonly CodeSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  const [closedFor, setClosedFor] = useState<string | null>(null);
  const byPerson = useRef(opts.byPerson);
  byPerson.current = opts.byPerson;
  const asked = useRef(0);
  const listId = useId();
  const sourceKey = opts.source ? JSON.stringify(opts.source) : '';

  useEffect(() => {
    const value = opts.value;
    const question = ++asked.current;
    // a row highlighted for the text before is not what Enter picks now
    setActive(-1);
    if (!allowed || value.trim().length < SUGGEST_MIN_CHARS) {
      setItems([]);
      return;
    }
    const timer = setTimeout(() => {
      if (!byPerson.current()) return;
      api<CodeSuggestion[]>('GET', suggestPath(warehouseId!, value, opts.source!))
        .then((found) => {
          if (asked.current !== question) return;
          setItems(found);
          setActive(-1);
        })
        .catch(() => undefined);
    }, SUGGEST_IDLE_MS);
    return () => clearTimeout(timer);
  }, [opts.value, allowed, warehouseId, sourceKey]);

  const open = allowed && items.length > 0 && closedFor !== opts.value && opts.value.trim().length >= SUGGEST_MIN_CHARS;
  const close = (value: string = opts.value) => {
    setClosedFor(value);
    setItems([]);
    setActive(-1);
  };
  const onKey = (event: KeyboardEvent<HTMLInputElement>, pick: (s: CodeSuggestion) => void) => {
    if (!open) return false;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(moveHighlight(active, event.key, items.length));
      return true;
    }
    if (event.key === 'Enter' && active >= 0 && items[active]) {
      event.preventDefault();
      pick(items[active]);
      setItems([]);
      setActive(-1);
      return true;
    }
    if (event.key === 'Escape') {
      // the list closes, not the dialog the field is in
      event.preventDefault();
      event.stopPropagation();
      close();
      return true;
    }
    return false;
  };
  return {
    items,
    active,
    open,
    onKey,
    close,
    listId,
    setActive,
    inputProps: opts.source
      ? {
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': open,
          'aria-controls': open ? listId : undefined,
          'aria-activedescendant': open && active >= 0 ? `${listId}-${active}` : undefined,
        }
      : {},
  };
}

/**
 * The suggestions under a field, picked by a tap, a click or the arrows and Enter. The field keeps
 * the cursor (a row takes no focus), so a reader still types into it while the list is open.
 */
export function SuggestList(props: { readonly suggestions: CodeSuggestions; readonly onPick: (s: CodeSuggestion) => void }) {
  const { suggestions, onPick } = props;
  if (!suggestions.open) return null;
  const kinds = new Set(suggestions.items.map((s) => s.kind));
  return (
    <div className="suggest-anchor">
      <ul className="suggest-list" id={suggestions.listId} role="listbox" aria-label="Öneriler">
        {suggestions.items.map((s, i) => {
          const text = suggestionText(s);
          return (
            <li
              key={`${s.kind}:${s.code}:${'productId' in s ? s.productId : ''}`}
              id={`${suggestions.listId}-${i}`}
              role="option"
              aria-selected={i === suggestions.active}
              className={i === suggestions.active ? 'on' : undefined}
              // the field keeps the cursor: a row is picked on release, not focused. Hovering highlights
              // nothing: a reader's Enter must never pick the row under a resting mouse
              onPointerDown={(e) => e.preventDefault()}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(s)}
            >
              <span className="suggest-main">
                {kinds.size > 1 && <span className="chip">{SUGGESTION_KIND_LABELS[s.kind]}</span>}
                <span className="mono">{text.main}</span>
              </span>
              {text.detail && <span className="suggest-detail">{text.detail}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
